import { useQueryClient, useMutation } from '@tanstack/react-query';
import { createDraft, finishDraft } from 'immer';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { courseToSimilarityKey } from '../utils/course-similarity.js';
import { getAllCoursesQuery } from './query.get-all.js';
import { updateCourseRequest } from './request.update-course.js';

/**
 * Optimistically rearranges a block in a course from one index to another.
 * In case anything goes wrong then the action is reverted.
 */
function useToggleBlockMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  return useMutation(toggleBlockMutation(client, notifier, user));
}
const toggleBlockMutation = (client, notifier, user) => {
  return {
    mutationKey: ["course-builder/toggle-block", user?.id],
    mutationFn: async input => {
      if (!user) {
        return;
      }
      const {
        rearrangedCourse
      } = toggleBlock(input);
      return updateCourseRequest(notifier, rearrangedCourse, user, {
        similarityKey: courseToSimilarityKey(input.course),
        includeBlocks: true
      });
    },
    onMutate: async input => {
      const {
        rearrangedCourse,
        existingIndex
      } = toggleBlock(input);
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), prev => prev?.map(it => it.id === input.course.id ? rearrangedCourse : it));
      await client.cancelQueries({
        queryKey: getAllCoursesQuery.queryKey({
          user
        })
      });
      return {
        reinsertAtIndex: existingIndex
      };
    },
    onSuccess: async result => {
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), prev => prev?.map(it => it.id === result?.id ? result : it));
    },
    onError: (error, input, context) => {
      notifier.notify({
        id: "course-builder.toggle-block.error",
        type: "error",
        body: context?.reinsertAtIndex === -1 ? "Elementet kunne ikke tilføjes. Prøv igen om lidt." : "Elementet kunne ikke fjernes. Prøv igen om lidt."
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), prev => prev?.map(it => it.id === input.course.id ? input.course : it));
    }
  };
};
function toggleBlock(input) {
  const draft = createDraft(input.course);
  draft.blocks ??= [];
  const existingIndex = draft.blocks.findIndex(it => it.id === input.block.id);
  if (existingIndex === -1) {
    draft.blocks.push(input.block);
  } else {
    draft.blocks.splice(existingIndex, 1);
  }
  return {
    rearrangedCourse: finishDraft(draft),
    existingIndex
  };
}

export { useToggleBlockMutation };
//# sourceMappingURL=mutation.toggle-block.js.map
